// Destination-side fin_transfer payload mapping.
//
// A transfer that ORIGINATED on NEAR is finalized on EVM/Solana/Starknet by
// submitting the NEAR `sign_transfer` MPC signature + payload to the
// destination bridge contract. The backend scrapes the `SignTransferEvent`
// (signature + message_payload) from the NEAR tx; this module maps that raw
// payload into each SDK's `buildFinalization` input and reconstructs the MPC
// signature bytes. These are pure functions (no wallet/network I/O) so they're
// unit-testable; the actual signing lives in dispatchFrontend.ts.

import { MPCSignature } from "@omni-bridge/near";
import { ChainKind } from "@omni-bridge/core";
import { BN } from "@coral-xyz/anchor";
import {
  PublicKey,
  SystemProgram,
  SYSVAR_CLOCK_PUBKEY,
  SYSVAR_RENT_PUBKEY,
  type TransactionInstruction,
} from "@solana/web3.js";
import type { SolanaBuilder } from "@omni-bridge/solana";
import type { SignTransferPayload } from "@/api/client";

type MessagePayload = SignTransferPayload["message_payload"];

/** message_payload chain name / contract wire name → SDK `ChainKind` enum.
 *  The event uses the contract's lowercase aliases (`near`, `eth`, `pol`,
 *  `strk`, `abs`, …). */
const CHAIN_KIND_BY_NAME: Record<string, ChainKind> = {
  eth: ChainKind.Eth,
  near: ChainKind.Near,
  sol: ChainKind.Sol,
  arb: ChainKind.Arb,
  base: ChainKind.Base,
  bnb: ChainKind.Bnb,
  btc: ChainKind.Btc,
  zcash: ChainKind.Zcash,
  pol: ChainKind.Pol,
  hlevm: ChainKind.HyperEvm,
  strk: ChainKind.Strk,
  abs: ChainKind.Abs,
  fogo: ChainKind.Fogo,
};

export function chainKindFromName(name: string): ChainKind {
  const k = CHAIN_KIND_BY_NAME[name.toLowerCase()];
  if (k === undefined) {
    throw new Error(`Unknown origin chain in sign_transfer payload: ${name}`);
  }
  return k;
}

/** Strip the `chain:` prefix off an OmniAddress, returning the bare address
 *  (`0x…` for EVM/Starknet, base58 for Solana). */
export function stripOmniPrefix(omniAddr: string): string {
  const idx = omniAddr.indexOf(":");
  return idx >= 0 ? omniAddr.slice(idx + 1) : omniAddr;
}

/** MPC signature → 65 bytes (r‖s‖v). EVM expects `recovery_id + 27`
 *  (`forEvm`); Solana/Starknet use the raw recovery id. */
export function mpcSignatureBytes(
  sig: SignTransferPayload["signature"],
  forEvm: boolean,
): Uint8Array {
  return MPCSignature.fromRaw(sig).toBytes(forEvm);
}

/** EVM `TransferPayload` (camelCase; `originChain` as the ChainKind number,
 *  addresses as bare `0x…`). */
export function evmFinalizationPayload(mp: MessagePayload) {
  return {
    destinationNonce: BigInt(mp.destination_nonce),
    originChain: chainKindFromName(mp.transfer_id.origin_chain),
    originNonce: BigInt(mp.transfer_id.origin_nonce),
    tokenAddress: stripOmniPrefix(mp.token_address) as `0x${string}`,
    amount: BigInt(mp.amount),
    recipient: stripOmniPrefix(mp.recipient) as `0x${string}`,
    feeRecipient: mp.fee_recipient ?? "",
  };
}

/** Starknet `StarknetTransferPayload` (camelCase; optional fee/message). */
export function starknetFinalizationPayload(mp: MessagePayload) {
  return {
    destinationNonce: BigInt(mp.destination_nonce),
    originChain: chainKindFromName(mp.transfer_id.origin_chain),
    originNonce: BigInt(mp.transfer_id.origin_nonce),
    tokenAddress: stripOmniPrefix(mp.token_address),
    amount: BigInt(mp.amount),
    recipient: stripOmniPrefix(mp.recipient),
    feeRecipient: mp.fee_recipient ?? undefined,
    message: mp.message && mp.message.length > 0 ? mp.message : undefined,
  };
}

/** The omni-bridge's native-SOL token marker = the System Program / default
 *  pubkey (`11111111111111111111111111111111`). A transfer whose
 *  `token_address` is this is native SOL, not an SPL token. */
export const NATIVE_SVM_MARKER = PublicKey.default.toBase58();

/** True when the SVM transfer's destination token is native SOL. The SDK's
 *  `buildFinalization` only handles SPL mints (it calls `isBridgedToken`,
 *  which rejects the System Program with "Not a valid SPL token mint"), so
 *  these route to the hand-built `finalize_transfer_sol` below. */
export function isNativeSvmToken(omniTokenAddress: string): boolean {
  return stripOmniPrefix(omniTokenAddress) === NATIVE_SVM_MARKER;
}

// The SVM builder methods/fields needed to assemble `finalize_transfer_sol`.
// The on-chain program ships this instruction, but @omni-bridge/solana 0.9.0
// never wired native SOL into `buildFinalization` (only into `buildTransfer`).
// All of these exist on the builder instance; only some are on its public
// type, so we reach in via a localized cast. The `common` (Wormhole) accounts
// mirror the SDK's own `buildTransfer` native-SOL branch exactly.
interface AnchorIxBuilder {
  accountsStrict(accounts: Record<string, unknown>): { instruction(): Promise<TransactionInstruction> };
}
interface SvmBuilderInternals {
  getProgram(): { methods: Record<string, (data: unknown) => AnchorIxBuilder> };
  deriveConfig(): PublicKey;
  deriveAuthority(): PublicKey;
  deriveSolVault(): PublicKey;
  deriveUsedNonces(destinationNonce: BN): PublicKey;
  deriveWormholeBridge(): PublicKey;
  deriveWormholeFeeCollector(): PublicKey;
  deriveWormholeSequence(): PublicKey;
  deriveShimMessage(): PublicKey;
  wormholeProgramId: PublicKey;
  shimProgramId: PublicKey;
  eventAuthorityId: PublicKey;
}

/** Hand-built native-SOL `finalize_transfer_sol` instruction (the SDK's
 *  `buildFinalization` only handles SPL mints). Mirrors the SDK's own
 *  `buildTransfer` native-SOL branch for the `common` accounts + the
 *  `finalize_transfer_sol` IDL account layout. Works for both Solana and Fogo
 *  builders (the program IDs differ; the instruction shape is identical). */
export async function buildNativeSvmFinalization(
  builder: SolanaBuilder,
  mp: MessagePayload,
  signatureBytes: Uint8Array,
  payer: PublicKey,
): Promise<TransactionInstruction[]> {
  const b = builder as unknown as SvmBuilderInternals;
  const destinationNonce = new BN(String(mp.destination_nonce));
  const payload = {
    destinationNonce,
    transferId: {
      originChain: Number(chainKindFromName(mp.transfer_id.origin_chain)),
      originNonce: new BN(String(mp.transfer_id.origin_nonce)),
    },
    amount: new BN(String(mp.amount)),
    feeRecipient: mp.fee_recipient ?? "",
  };
  const config = b.deriveConfig();
  const common = {
    payer,
    config,
    bridge: b.deriveWormholeBridge(),
    feeCollector: b.deriveWormholeFeeCollector(),
    sequence: b.deriveWormholeSequence(),
    clock: SYSVAR_CLOCK_PUBKEY,
    rent: SYSVAR_RENT_PUBKEY,
    systemProgram: SystemProgram.programId,
    wormholeProgram: b.wormholeProgramId,
    message: b.deriveShimMessage(),
    wormholePostMessageShim: b.shimProgramId,
    wormholePostMessageShimEa: b.eventAuthorityId,
  };
  const instruction = await b
    .getProgram()
    .methods.finalizeTransferSol({ payload, signature: [...signatureBytes] })
    .accountsStrict({
      config,
      usedNonces: b.deriveUsedNonces(destinationNonce),
      authority: b.deriveAuthority(),
      recipient: new PublicKey(stripOmniPrefix(mp.recipient)),
      solVault: b.deriveSolVault(),
      common,
      systemProgram: SystemProgram.programId,
    })
    .instruction();
  return [instruction];
}

/** Solana/Fogo `SolanaTransferMessagePayload` (snake_case; nested transfer_id).
 *
 *  Unlike EVM/Starknet, the SVM builder's `buildFinalization` calls
 *  `extractSvmAddress(recipient/token_address, this.chain)`, which expects the
 *  FULL OmniAddress (`sol:…` / `fogo:…`) — it validates the prefix matches the
 *  builder's chain and strips it itself. So we must NOT pre-strip here (doing
 *  so yields "Invalid OmniAddress format" / "Address must be on …"). */
export function solanaFinalizationPayload(mp: MessagePayload) {
  return {
    destination_nonce: BigInt(mp.destination_nonce),
    transfer_id: {
      origin_chain: chainKindFromName(mp.transfer_id.origin_chain),
      origin_nonce: BigInt(mp.transfer_id.origin_nonce),
    },
    token_address: mp.token_address,
    amount: BigInt(mp.amount),
    recipient: mp.recipient,
    fee_recipient: mp.fee_recipient ?? null,
  };
}
